'use client'

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import './StaggeredMenu.css'

export type StaggeredMenuItem = {
  label: string
  link: string
  ariaLabel?: string
}

type Props = {
  position?: 'left' | 'right'
  colors?: string[]
  items?: StaggeredMenuItem[]
  displayItemNumbering?: boolean
  accentColor?: string
  menuButtonColor?: string
  openMenuButtonColor?: string
  changeMenuColorOnOpen?: boolean
  closeOnClickAway?: boolean
  ctaHref?: string
  ctaLabel?: string
  openLabel?: string
  closeLabel?: string
  onMenuOpen?: () => void
  onMenuClose?: () => void
}

export function StaggeredMenu({
  position = 'right',
  colors = ['#4F35D6', '#2B5AFF'],
  items = [],
  displayItemNumbering = false,
  accentColor = '#2B5AFF',
  menuButtonColor = '#0F172A',
  openMenuButtonColor = '#0F172A',
  changeMenuColorOnOpen = false,
  closeOnClickAway = true,
  ctaHref = '#contact',
  ctaLabel = "Let's Talk",
  openLabel = 'Menu',
  closeLabel = 'Close',
  onMenuOpen,
  onMenuClose,
}: Props) {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const panelRef = useRef<HTMLElement>(null)
  const preLayersRef = useRef<HTMLDivElement>(null)
  const preLayerElsRef = useRef<HTMLElement[]>([])
  const plusHRef = useRef<HTMLSpanElement>(null)
  const plusVRef = useRef<HTMLSpanElement>(null)
  const iconRef = useRef<HTMLSpanElement>(null)
  const textInnerRef = useRef<HTMLSpanElement>(null)
  const [textLines, setTextLines] = useState([openLabel, closeLabel])

  const openTlRef = useRef<gsap.core.Timeline | null>(null)
  const closeTweenRef = useRef<gsap.core.Tween | null>(null)
  const spinTweenRef = useRef<gsap.core.Tween | null>(null)
  const textCycleAnimRef = useRef<gsap.core.Tween | null>(null)
  const colorTweenRef = useRef<gsap.core.Tween | null>(null)
  const toggleBtnRef = useRef<HTMLButtonElement>(null)
  const busyRef = useRef(false)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current
      const preContainer = preLayersRef.current
      const plusH = plusHRef.current
      const plusV = plusVRef.current
      const icon = iconRef.current
      const textInner = textInnerRef.current
      if (!panel || !plusH || !plusV || !icon || !textInner) return

      const preLayers = preContainer
        ? Array.from(preContainer.querySelectorAll<HTMLElement>('.sm-prelayer')): []
      preLayerElsRef.current = preLayers

      const offscreen = position === 'left' ? -100 : 100
      gsap.set([panel, ...preLayers], { x: 0, xPercent: offscreen, opacity: 1 })
      if (preContainer) gsap.set(preContainer, { x: 0, xPercent: 0, opacity: 1 })
      gsap.set(plusH, { transformOrigin: '50% 50%', rotate: 0 })
      gsap.set(plusV, { transformOrigin: '50% 50%', rotate: 90 })
      gsap.set(icon, { rotate: 0, transformOrigin: '50% 50%' })
      gsap.set(textInner, { yPercent: 0 })
      if (toggleBtnRef.current) gsap.set(toggleBtnRef.current, { color: menuButtonColor })
    })
    return () => ctx.revert()
  }, [menuButtonColor, position])

  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current
    const layers = preLayerElsRef.current
    if (!panel) return null

    openTlRef.current?.kill()
    closeTweenRef.current?.kill()
    closeTweenRef.current = null

    const itemEls = Array.from(panel.querySelectorAll<HTMLElement>('.sm-panel-itemLabel'))
    const numberEls = Array.from(
      panel.querySelectorAll<HTMLElement>('.sm-panel-list[data-numbering] .sm-panel-item'),
    )

    const offscreen = position === 'left' ? -100 : 100
    if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 })
    if (numberEls.length) gsap.set(numberEls, { '--sm-num-opacity': 0 })

    const tl = gsap.timeline({ paused: true })

    layers.forEach((el, i) => {
      tl.fromTo(el, { xPercent: offscreen }, { xPercent: 0, duration: 0.5, ease: 'power4.out' }, i * 0.07)
    })
    const lastTime = layers.length ? (layers.length - 1) * 0.07 : 0
    const panelStart = lastTime + (layers.length ? 0.08 : 0)
    const panelDuration = 0.65

    tl.fromTo(
      panel,
      { xPercent: offscreen },
      { xPercent: 0, duration: panelDuration, ease: 'power4.out' },
      panelStart,
    )

    if (itemEls.length) {
      const itemsStart = panelStart + panelDuration * 0.15
      tl.to(
        itemEls,
        { yPercent: 0, rotate: 0, duration: 1, ease: 'power4.out', stagger: { each: 0.1, from: 'start' } },
        itemsStart,
      )
      if (numberEls.length) {
        tl.to(
          numberEls,
          { duration: 0.6, ease: 'power2.out', '--sm-num-opacity': 1, stagger: { each: 0.08, from: 'start' } },
          itemsStart + 0.1,
        )
      }
    }

    openTlRef.current = tl
    return tl
  }, [position])

  const playOpen = useCallback(() => {
    if (busyRef.current) return
    busyRef.current = true
    const tl = buildOpenTimeline()
    if (tl) {
      tl.eventCallback('onComplete', () => { busyRef.current = false })
      tl.play(0)
    } else {
      busyRef.current = false
    }
  }, [buildOpenTimeline])

  const playClose = useCallback(() => {
    openTlRef.current?.kill()
    openTlRef.current = null
    const panel = panelRef.current
    const layers = preLayerElsRef.current
    if (!panel) return

    const offscreen = position === 'left' ? -100 : 100
    closeTweenRef.current?.kill()
    closeTweenRef.current = gsap.to([...layers, panel], {
      x: 0,
      xPercent: offscreen,
      duration: 0.32,
      ease: 'power3.in',
      overwrite: 'auto',
      onComplete: () => {
        const els = Array.from(panel.querySelectorAll<HTMLElement>('.sm-panel-itemLabel'))
        if (els.length) gsap.set(els, { yPercent: 140, rotate: 10 })
        busyRef.current = false
      },
    })
  }, [position])

  const animateIcon = useCallback((opening: boolean) => {
    const icon = iconRef.current
    if (!icon) return
    spinTweenRef.current?.kill()
    spinTweenRef.current = opening
      ? gsap.to(icon, { rotate: 225, duration: 0.8, ease: 'power4.out', overwrite: 'auto' })
      : gsap.to(icon, { rotate: 0, duration: 0.35, ease: 'power3.inOut', overwrite: 'auto' })
  }, [])

  const animateColor = useCallback(
    (opening: boolean) => {
      const btn = toggleBtnRef.current
      if (!btn) return
      colorTweenRef.current?.kill()
      if (changeMenuColorOnOpen) {
        colorTweenRef.current = gsap.to(btn, {
          color: opening ? openMenuButtonColor : menuButtonColor,
          delay: 0.18,
          duration: 0.3,
          ease: 'power2.out',
        })
      }
    },
    [openMenuButtonColor, menuButtonColor, changeMenuColorOnOpen],
  )

  const animateText = useCallback((opening: boolean) => {
    const inner = textInnerRef.current
    if (!inner) return
    textCycleAnimRef.current?.kill()

    const currentLabel = opening ? openLabel : closeLabel
    const cycles = 3
    const seq = [currentLabel]
    let last = currentLabel
    for (let i = 0; i < cycles; i++) {
      last = last === openLabel ? closeLabel : openLabel
      seq.push(last)
    }
    const target = opening ? closeLabel : openLabel
    if (last !== target) seq.push(target)
    setTextLines(seq)

    gsap.set(inner, { yPercent: 0 })
    const finalShift = ((seq.length - 1) / seq.length) * 100
    textCycleAnimRef.current = gsap.to(inner, {
      yPercent: -finalShift,
      duration: 0.5 + seq.length * 0.07,
      ease: 'power4.out',
    })
  }, [openLabel, closeLabel])

  const toggleMenu = useCallback(() => {
    const target = !openRef.current
    openRef.current = target
    setOpen(target)
    if (target) { onMenuOpen?.(); playOpen() }
    else { onMenuClose?.(); playClose() }
    animateIcon(target)
    animateColor(target)
    animateText(target)
  }, [playOpen, playClose, animateIcon, animateColor, animateText, onMenuOpen, onMenuClose])

  const closeMenu = useCallback(() => {
    if (!openRef.current) return
    openRef.current = false
    setOpen(false)
    onMenuClose?.()
    playClose()
    animateIcon(false)
    animateColor(false)
    animateText(false)
  }, [playClose, animateIcon, animateColor, animateText, onMenuClose])

  React.useEffect(() => {
    if (!closeOnClickAway || !open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Element
      if (!panelRef.current?.contains(t) && !toggleBtnRef.current?.contains(t)) closeMenu()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [closeOnClickAway, open, closeMenu])

  const raw = colors.length ? colors.slice(0, 4) : ['#4F35D6', '#2B5AFF']
  const layerColors = raw.length >= 3 ? [...raw.slice(0, Math.floor(raw.length / 2)), ...raw.slice(Math.floor(raw.length / 2) + 1)] : raw

  return (
    <div
      className="staggered-menu-wrapper fixed-wrapper"
      style={accentColor ? ({ '--sm-accent': accentColor } as React.CSSProperties) : undefined}
      data-position={position}
      data-open={open || undefined}
    >
      <div ref={preLayersRef} className="sm-prelayers" aria-hidden="true">
        {layerColors.map((c, i) => (
          <div key={i} className="sm-prelayer" style={{ background: c }} />
        ))}
      </div>

      <header className="staggered-menu-header" aria-label="Mobile nav toggle">
        <button
          ref={toggleBtnRef}
          className="sm-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={toggleMenu}
          type="button"
        >
          <span ref={iconRef} className="sm-icon" aria-hidden="true">
            <span ref={plusHRef} className="sm-icon-line" />
            <span ref={plusVRef} className="sm-icon-line" />
          </span>
          <span className="sm-toggle-textWrap" aria-hidden="true">
            <span ref={textInnerRef} className="sm-toggle-textInner">
              {textLines.map((l, i) => (
                <span className="sm-toggle-line" key={i}>{l}</span>
              ))}
            </span>
          </span>
        </button>
      </header>

      <aside
        ref={panelRef}
        className="staggered-menu-panel"
        aria-label="Mobile navigation"
        inert={!open}
      >
        <div className="sm-panel-inner">
          <ul className="sm-panel-list" role="list" data-numbering={displayItemNumbering || undefined}>
            {items.map((it, idx) => (
              <li className="sm-panel-itemWrap" key={it.label + idx}>
                <a
                  className="sm-panel-item"
                  href={it.link}
                  aria-label={it.ariaLabel}
                  data-index={idx + 1}
                  onClick={closeMenu}
                  {...(it.link.startsWith('http') ? { target: '_blank', rel: 'noopener' } : {})}
                >
                  <span className="sm-panel-itemLabel">{it.label}</span>
                </a>
              </li>
            ))}
          </ul>
          <a href={ctaHref} className="sm-cta-btn" onClick={closeMenu}>
            {ctaLabel}
          </a>
        </div>
      </aside>
    </div>
  )
}

export default StaggeredMenu
